
import breakpoints from '../../styles/variables/exports/breakpoints.scss?module'
import { overflowContainerMixin } from '../../mixins'

export default {
  name: 'EoImg',
  mixins: [ overflowContainerMixin ],
  props: {
    /**
     * Image source
     */
    src: {
      type: String,
      default: null,
    },
    /**
     * Performance: rendered width and height of the image (400x300)
     */
    size: {
      type: [String, Number],
      default: null,
    },
    /**
     * Performance: CSS width of the image (vw) (calc) (px) (em) (rem)
     */
    width: {
      type: [String, Number],
      default: null,
    },
    /**
     * Focus point of the image to center on when cropped (x% y%)
     */
    focus: {
      type: [String, Object],
      default: '50% 50%',
    },
    /**
     * Make a cut out of the image (circle) (rounded)
     */
    frame: {
      type: String,
      validator: prop => ['circle', 'rounded', null].includes(prop),
      default: null,
    },
    /**
     * Invisible description for screen readers
     */
    alt: {
      type: String,
      default: null,
    },
    /**
     * Loading attribute: (eager) (lazy)
     */
    loading: {
      type: String,
      validator: prop => ['eager', 'lazy', '', null].includes(prop),
      default: 'lazy',
    },
  },
  data () {
    return {
      isLoading: true,
    }
  },
  computed: {
    cClasses () {
      return {
        'eo-img': true,
        'is-loading': this.isLoading,
        ['is-frame-' + this.frame]: !!this.frame,
      }
    },
    cStyles () {
      return {
        '--focus': this.cFocus,
        marginLeft: this.overflowOffset ? `-${this.overflowOffset}px` : null,
        marginRight: this.overflowOffset ? `-${this.overflowOffset}px` : null,
        maxWidth: this.overflowOffset ? 'none' : null,
        width: this.overflowOffset ? `calc(100% + ${this.overflowOffset * 2}px)` : null,
      }
    },
    cSources () {
      if (!this.src) return
      let sizes = this.$viewport.parseOptions(this.size)
      if (!sizes) return

      let sources = []
      let sizesObject = typeof sizes === 'object' ? sizes : {xsAndUp: sizes}
      let widestOption = null

      // Create source for every given size
      for (const viewport in sizesObject) {
        const width = this.parseToInteger(sizesObject[viewport]?.split('x')[0])
        sources.push({
          url: this.getOptimizedSrc(width),
          width: width,
        })

        if (width > widestOption || !widestOption) {
          widestOption = width
        }

        // Add a retina version of the largest width
        sources.push({
          url: this.getOptimizedSrc(widestOption * 2),
          width: widestOption * 2,
        })
      }

      return sources
    },
    cSrc () {
      if (!this.cSources || !this.cSources.length) {
        return this.getOptimizedSrc(this.cWidth)
      } else {
        return this.cSources[this.cSources.length - 1]?.url
      }
    },
    cSrcset () {
      if (!this.cSources || !this.cSources.length) return
      let srcset = this.cSources.map(source => {
        return `${source.url} ${source.width}w`
      })
      return srcset.join(', ')
    },
    cSizes () {
      let options
      if (this.widthCss) {
        options = this.$viewport.parseOptions(this.widthCss)
        if (!options) return
      } else {
        // Fallback: get sizes based on prop 'width'
        options = this.$viewport.parseOptions(this.width)
        if (!options || typeof options === 'string' || typeof options === 'number') return
      }

      let cSizes = []
      for (const viewport in options) {
        const width = options[viewport]
        const breakpoint = viewport.replace('AndUp', '')
        cSizes.push(`(min-width: ${breakpoints[breakpoint]}) ${isNaN(width) ? width : `${width}px`}`)
      }
      return cSizes.join(', ')
    },
    cHeight () {
      return this.$viewport.switch(this.size)?.split('x')[1]
    },
    cWidth () {
      return this.$viewport.switch(this.size)?.split('x')[0]
    },
    cFocus () {
      if (!this.focus) return
      if (typeof this.focus === 'object') {
        const aspectRatio = this.parseToInteger(this.cHeight) / this.parseToInteger(this.cWidth)
        let orientation = aspectRatio == 1 ? 'square' : orientation
        orientation = aspectRatio > 1 ? 'portrait' : orientation
        orientation = aspectRatio < 1 ? 'landscape' : orientation
        return this.focus[orientation]
      }
      return this.focus
    },
  },
  methods: {
    getOptimizedSrc (width) {
      if (!this.src) return ''

      let src = null
      if (!this.src.startsWith('http')) {
        // Image is hosted internally
        if (this.$library.baseUrl && !this.$library.baseUrl.includes(':')) {
          // Image is hosted by Nuxt webserver, so prepend baseUrl
          src = `${this.$library.baseUrl}${this.src}`
        } else {
          // Image is hosted by Nuxt localhost server, so don't optimize
          return this.src
        }
      } else {
        // Image is hosted externally
        src = this.src
      }

      if (this.src.includes('cdn.eo.nl') || this.src.includes('b-cdn.net')) {
        // Image is hosted by Prepr CDNs, so use its resizer
        return width ? this.src.replace('{format}', `w_${width}`) : this.src
      } else if (src.includes('images.poms.omroep.nl/image')) {
        // Image is hosted by POMS CDNs, so use its resizer
        // POMS CDN has a limited width on images and gives 500 error above "maxPOMSWidth" threshold
        // Sadly no 4k native images are supported by POMS (which are 3840px wide)

        const maxPOMSWidth = 3000 
        const currentWidth = width >= maxPOMSWidth ? maxPOMSWidth : width
        return  currentWidth ? this.src.replace('/image/', `/image/s${currentWidth}/`) : this.src
      } else {
        // Image is hosted elsewhere, so use Cloudflare resizer
        const cdn = this.$library.cloudflareCdn
        && !this.$library.cloudflareCdn.includes('feat.eo.nl')
        && !this.$library.cloudflareCdn.includes('staging.eo.nl')
        && !this.$library.cloudflareCdn.includes('localhost')
        && !this.$library.cloudflareCdn.includes('local.eo.nl')
        ? this.$library.cloudflareCdn : 'https://www.eo.nl/cdn-image'
        const sourceParam = src ? `&s=${encodeURIComponent(src)}` : ''
        const widthParam = width ? `&w=${width}` : ''

        return `${cdn}?${widthParam}${sourceParam}`
      }
    },
    parseToInteger (width) {
      width = width.toString()
      if (width.endsWith('px')) {
        // Return same value
        return Math.round(width.replace('px', ''))
      }
      if (width.endsWith('em') || width.endsWith('rem')) {
        // Calculate based on default body font size
        return Math.round(width.replace('em', '').replace('rem', '') * 18)
      }
      return Math.round(width)
    },
    onLoaded (event) {
      this.isLoading = false
      this.$emit('load', event)
    },
  },
}
